"use client"
import React from 'react'
import { cn } from '@/shared/utils/tw'
import { useLanguageSelect } from '../model/use-language-select'
import { LanguageSelectProps } from '../types/language-select'
import { LANGUAGE_OPTIONS } from '../const/language-options'
import { useI18n } from '@/shared/hooks/useI18n'

const LanguageSelect: React.FC<LanguageSelectProps> = ({
  value,
  onChange,
  className,
  disabled = false,
  variant = 'dark'
}) => {
  const { currentLanguage: i18nCurrentLanguage } = useI18n()
  const isLight = variant === 'light'

  // i18n에서 현재 언어를 가져오거나 props의 value 사용
  const effectiveValue = value || i18nCurrentLanguage || 'ko'

  const {
    isOpen,
    selectedLanguage,
    selectRef,
    toggleDropdown,
    selectLanguage
  } = useLanguageSelect({
    initialValue: effectiveValue,
    onLanguageChange: onChange
  })

  // 현재 선택된 언어 옵션 찾기
  const selectedOption = LANGUAGE_OPTIONS.find(option => option.value === selectedLanguage)
  const currentLanguage = selectedOption || LANGUAGE_OPTIONS[0]

  return (
    <div ref={selectRef} className={cn("relative", className)}>
      {/* 언어 선택 버튼 (Figma: 3152:3249) */}
      <button
        type="button"
        onClick={disabled ? undefined : toggleDropdown}
        disabled={disabled}
        className={cn(
          "flex items-center justify-center",
          "rounded-full",
          "backdrop-blur-[9.4px]",
          "[-webkit-backdrop-filter:blur(9.4px)]",
          "focus:outline-none",
          disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer transition-all duration-200"
        )}
        style={{
          padding: '8px 10px',
          gap: '4px',
          background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.15)',
          border: isLight ? '1.7px solid rgba(0,0,0,0.1)' : '1.7px solid rgba(255,255,255,0.2)',
        }}
      >
        {/* 지구 아이콘 */}
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={cn("shrink-0", isLight ? "text-[#222222]" : "text-white")}>
          <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2.5 10H17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 2.5C12.0844 4.77137 13.269 7.32105 13.3333 10C13.269 12.679 12.0844 15.2286 10 17.5C7.91557 15.2286 6.73098 12.679 6.66667 10C6.73098 7.32105 7.91557 4.77137 10 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>

        {/* 언어 텍스트 */}
        <span className={cn("font-pretendard font-semibold text-[14px] leading-[1.3] whitespace-nowrap", isLight ? "text-[#222222]" : "text-white")}>
          {currentLanguage.label}
        </span>
      </button>

      {/* 드롭다운 메뉴 */}
      {isOpen && !disabled && (
        <div
          className={cn(
            "absolute right-0 z-[9999]",
            "backdrop-blur-[9.4px]",
            "[-webkit-backdrop-filter:blur(9.4px)]",
            "flex flex-col items-center"
          )}
          style={{
            top: 'calc(100% + 4px)',
            minWidth: '100%',
            borderRadius: '16px',
            background: isLight ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.15)',
            border: isLight ? '1.7px solid rgba(0,0,0,0.1)' : '1.7px solid rgba(255,255,255,0.2)',
            padding: '8px',
            gap: '4px',
          }}
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={(e) => selectLanguage(option.value, e)}
              className={cn(
                "w-full text-center",
                "font-pretendard font-semibold text-[14px] leading-[1.3]",
                "rounded-[10px]",
                "transition-all duration-200",
                "focus:outline-none cursor-pointer",
                selectedLanguage === option.value
                  ? (isLight ? "text-[#222222] bg-[rgba(0,0,0,0.06)]" : "text-white bg-[rgba(255,255,255,0.15)]")
                  : (isLight
                      ? "text-black/50 hover:text-[#222222] hover:bg-[rgba(0,0,0,0.04)]"
                      : "text-[rgba(255,255,255,0.6)] hover:text-white hover:bg-[rgba(255,255,255,0.08)]")
              )}
              style={{ padding: '6px 10px' }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSelect
